import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, logout as logoutApi, setAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, try to hydrate session from the httpOnly cookie or in-memory token
  useEffect(() => {
    getMe()
      .then((res) => {
        setUser(res.data);
      })
      .catch(() => {
        // No valid session — user needs to log in
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((token, userData) => {
    // Store token in memory for requests (NOT in localStorage)
    // This provides a fallback when httpOnly cookies don't work
    // across production proxy layers
    if (token) {
      setAuthToken(token);
    }
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // Ignore errors — clear local state regardless
    }
    setAuthToken(null);
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'Admin';
  const isManager = user?.role === 'Manager' || isAdmin;
  const isAccountant = user?.role === 'Accountant' || isManager;
  const isStaff = user?.role === 'Staff' || isAccountant;
  const isViewer = user?.role === 'Viewer' || isStaff;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isManager, isAccountant, isStaff, isViewer }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
